import { shouldAnimate, transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, h3, h4, label, link, paragraphRegular } from '@helpers/typography';
import svgTick from '@img/icons/background/tick.svg?url';
import styled, { css } from 'styled-components';
import { ButtonStyles } from '../Buttons/GeneralButton/Button.styles';
const Container = styled.section `
  --invalidColor: ${brand.validation.invalid};

  display: block;
`;
const Form = styled.form `
  display: block;
`;
const Fieldset = styled.fieldset `
  display: block;

  & + & {
    margin: ${fluid(20, 22)} 0 0;
  }
`;
const Legend = styled.legend `
  ${h4};

  color: ${brand.primary.purple};
  display: block;
  margin: ${fluid(20, 22)} 0 12px;
  text-transform: uppercase;
`;
const Field = styled.div `
  ${h4};

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${fluid(10, 12)};
  margin: 0 0 ${fluid(20, 22)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const Label = styled.label `
  ${label};

  color: ${brand.primary.purple};
  margin: 0;
  position: relative;
`;
const ToolTipContainer = styled.div `
  position: absolute;
  left: calc(100% + 10px);
  top: -5px;
`;
const ToolTip = styled.button `
  border: 1px solid ${brand.primary.purple};
  background-color: ${brand.white};
  color: ${brand.primary.purple};
  border-radius: 10000px;
  font-size: 11px;
  padding: 2px;
  width: 18px;
  height: 18px;
`;
const ToolTipInner = styled.div `
  text-transform: none;
  border: none;
  background-color: ${brand.primary.purple};
  color: ${brand.primary.offWhite};
  font-size: 14px;
  width: 170px;
  padding: 5px 20px 5px 5px;
  border-radius: 4px;
  position: absolute;
  top: calc(100% + 7px);
  right: 0;
  display: none;
  z-index: 2;

  @media ${from(Device.TabletLarge)} {
    right: auto;
    left: 0;
  }

  &[data-show-tooltip='true'] {
    display: block;
  }

  &:after {
    bottom: 100%;
    right: 4px;
    border: solid transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(136, 183, 213, 0);
    border-bottom-color: ${brand.primary.purple};
    border-width: 5px;
    margin-left: -5px;

    @media ${from(Device.TabletLarge)} {
      right: auto;
      left: 9px;
    }
  }
`;
const CloseToolTip = styled.button `
  border: none;
  background: transparent;
  position: absolute;
  top: 2px;
  right: 0;
  color: ${brand.primary.offWhite};

  svg {
    width: 10px;
  }
`;
const ControlLabel = styled.label `
  font-weight: 400;
  display: flex;
  align-items: center;
  height: 30px;

  > a {
    ${link};
  }

  > input {
    ${srOnly};
  }
`;
const CheckboxLabel = styled.span `
  ${label};

  text-transform: none;
  position: relative;
  padding-left: 42px;
  margin: 0;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    display: block;
    height: 28px;
    width: 28px;
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '';
    height: 30px;
    opacity: 0;
    width: 30px;
    color: ${brand.primary.purple};
    background-image: url(${svgTick});
    background-size: 16px;
    background-repeat: no-repeat;
    background-position: center;
  }

  &::before {
    background-color: ${brand.white};
    border: 1px solid ${brand.primary.purple};
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  [data-error='true'] &::before {
    border-color: var(--invalidColor);
  }
`;
const RadioLabel = styled.span `
  ${label};

  text-transform: none;
  position: relative;
  padding-left: 42px;
  margin: 0;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    display: block;
    border-radius: 50%;
    height: 28px;
    width: 28px;
    transform: translateY(-50%);
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '';
    height: 30px;
    opacity: 0;
    width: 30px;
    color: ${brand.primary.purple};
    background-image: url(${svgTick});
    background-size: 16px;
    background-repeat: no-repeat;
    background-position: center;
  }

  &::before {
    background-color: ${brand.white};
    border: 1px solid ${brand.primary.purple};
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  [data-error='true'] &::before {
    border-color: var(--invalidColor);
  }
`;
const sharedInputStyles = css `
  ${paragraphRegular};

  display: block;
  background: ${brand.white};
  border: none;
  border-bottom: 1px solid ${brand.primary.purple};
  background-color: ${brand.white};
  color: ${brand.primary.purple};
  height: ${fluid(46, 50)};
  padding: 18px 16px;
  width: 100%;
  margin: 0;

  [data-error='true'] & {
    border-color: ${brand.validation.invalid};
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 5px;
  }
`;
const Input = styled.input `
  ${sharedInputStyles};
`;
const SelectWrapper = styled.div `
  width: 100%;
  position: relative;
`;
const Arrow = styled.div `
  ${transition('color')};

  display: grid;
  place-items: center;
  height: 50px;
  width: 32px;
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;

  > svg {
    ${transition('transform')};

    display: block;
    height: 12px;
    color: currentColor;
    transform: rotate(90deg);
  }

  [data-error='true'] & {
    color: var(--invalidColor);
  }
`;
const Select = styled.select `
  ${sharedInputStyles};

  appearance: none;
  padding-right: 32px;
  padding-top: 0;
  padding-bottom: 0;

  option {
    background-color: var(--bgColor);
    color: var(--fgColor);
  }

  &:focus ~ ${Arrow} > svg {
    transform: rotate(-90deg);
  }
`;
const Textarea = styled.textarea `
  ${sharedInputStyles};

  height: auto;
  min-height: 163px;
`;
const Error = styled.span `
  font-weight: 400;

  display: block;
  font-size: 12px;
  line-height: 22px;
  margin: -4px 0 0;
  color: var(--invalidColor);
`;
const Submit = styled.div `
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 24px 0 0;

  ${ButtonStyles.Icon} {
    svg {
      ${shouldAnimate(css `
        animation: 6s linear infinite rotate;
      `)}
    }

    path {
      stroke-width: 1.5;
    }
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;
const Markup = styled.div `
  ${contentStyles};
`;
const ErrorList = styled.ul `
  display: block;
  margin: 0 0 ${fluid(32, 48)};
  color: ${brand.validation.invalid};
`;
const ErrorListItem = styled.li `
  ${paragraphRegular};

  margin: 0 0 ${fluid(8, 12)};
`;
const FieldTitle = styled.h2 `
  ${h3};

  color: ${brand.primary.purple};
  margin-bottom: 0;
  display: block;
  width: 100%;
`;
const FieldDescription = styled.p `
  ${paragraphRegular};
  color: ${brand.primary.purple};
  display: block;
  width: 100%;
  margin: 0 0 12px;
`;
const List = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${fluid(8, 12)};
`;
const RecaptchaWrapper = styled.div `
  width: 100%;
`;
// File Upload Styles
const FileUploadLabelContainer = styled.div `
  display: flex;
  gap: ${fluid(8, 12)};
  align-items: center;
  width: 100%;
  overflow: hidden;

  > label {
    ${label};

    color: ${brand.primary.purple};
    text-decoration: none;
    margin: 0;
    white-space: nowrap;

    &:hover {
      cursor: pointer;
    }

    > svg {
      display: block;
      height: ${fluid(16, 18)};
      width: ${fluid(16, 18)};
      margin-right: ${fluid(6, 8)};
    }
  }
`;
const FileNameContent = styled.span `
  ${fonts.graphikCompact.regular};

  display: block;
  font-size: ${fluid(14, 16)};
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${brand.secondary.red};
  max-width: 180px;

  @media ${from(Device.TabletLarge)} {
    max-width: 250px;
  }
`;
const DisabledFormContent = styled.div ``;
export default {
    Arrow,
    CheckboxLabel,
    CloseToolTip,
    Container,
    ControlLabel,
    DisabledFormContent,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    FieldDescription,
    Fieldset,
    FieldTitle,
    FileNameContent,
    FileUploadLabelContainer,
    Form,
    Input,
    Label,
    Legend,
    List,
    Markup,
    RadioLabel,
    RecaptchaWrapper,
    Select,
    SelectWrapper,
    Submit,
    ToolTip,
    ToolTipContainer,
    ToolTipInner,
    Textarea,
};
